import { useAtom } from '@reatom/npm-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { PremiumText } from '@root/entities/premium/ui'
import {
  HeadsetIcon,
  NotificationBellIcon,
  PremiumGradientIcon,
  WalletTrackerAlertIcon,
} from '@root/shared/ui-kit'
import { notificationCenterUpdatesAtom } from '@root/widgets/notification-center/atoms'

const ICON_SIZE = 32

export const NotificationCenterUpdates: React.FC = () => {
  const { t } = useTranslation()
  const [updates] = useAtom(notificationCenterUpdatesAtom)

  const notificationMap = {
    walletTracker: {
      icon: <WalletTrackerAlertIcon size={ICON_SIZE} />,
      title: t('wallet-tracker'),
      count: updates.walletTracker,
    },
    premium: {
      icon: <PremiumGradientIcon size={ICON_SIZE} />,
      title: <PremiumText>{t('premium.feed')}</PremiumText>,
      count: updates.premium,
    },
    notifications: {
      icon: <NotificationBellIcon size={ICON_SIZE} />,
      title: t('notification-center.notifications'),
      count: updates.notifications,
    },
    support: {
      icon: <HeadsetIcon size={ICON_SIZE} />,
      title: t('support'),
      count: updates.support,
    },
  }

  const hasUpdates = Object.values(notificationMap).some(
    (value) => value.count > 0,
  )

  if (!hasUpdates) {
    return undefined
  }

  return (
    <Root>
      {Object.entries(notificationMap)
        .filter(([_, value]) => value.count > 0)
        .map(([key, value]) => (
          <UpdateItem key={key} data-type={key}>
            <Icon>
              {value.icon}
              {value.title}
            </Icon>
            <Label data-type={key}>
              {t('notification-center.updates', { count: value.count })}
            </Label>
          </UpdateItem>
        ))}
      {Object.entries(notificationMap)
        .filter(([_, value]) => value.count === 0)
        .map(([key]) => (
          <span key={key} />
        ))}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 5px;
`

const UpdateItem = styled.div`
  background: var(--backgroundA-2);

  border-radius: 10px;
  display: grid;
  gap: 12px;
  height: 102px;
  min-width: 0;

  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  padding: 3px;
  gap: 4px;

  &[data-type='walletTracker'] {
    color: var(--amber-9);
  }
  &[data-type='notifications'] {
    color: var(--text-11);
  }
  &[data-type='support'] {
    color: var(--text-11);
  }
`

const Icon = styled.div`
  background: var(--background-1);

  display: grid;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 8px;
  height: 72px;
  min-width: 0;
  gap: 2px;

  justify-items: center;
  text-align: center;

  line-height: 12px;
  font-weight: 500;
`

const Label = styled.div`
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;

  &[data-type='premium'] {
    color: var(--premium-9);
  }
  &[data-type='notifications'] {
    color: var(--text-12);
  }
  &[data-type='walletTracker'] {
    color: var(--amber-9);
  }
  &[data-type='support'] {
    color: var(--candleGreen-9);
  }
`
